import ITask from './ITask';
import WorldSystem from '../../System/World/WorldSystem';
import Game from 'Game';
import { IGameState } from 'State/Game/IGameState';
import { PlaySound } from 'System/Audio/AudioHelper';
import { WorldDataHelper } from 'Utility/WorldDataHelper';
import { IFieldData } from 'State/World/IFieldData';
import GameObject from 'GameObject/GameObject';
import WorldHUDHandler from 'System/UI/HUD/WorldHUDHandler';
import { StartAnimationClip } from 'System/Animation/AnimationHelper';
import { Counter } from 'System/Animation/Clip';
import CoinBar, { hackForceCurrencyAnimation } from 'Prefabs/UI/HUD/CoinBar';
import { PlayerActions } from 'State/Player/PlayerActions';
import { FTUXStateHelper } from 'State/Game/FTUX/FTUXStateHelper';
import { FTUXType } from 'Player/PlayerProperty/Progress/FTUXType';
import { PlayerStateHelper } from 'State/Player/PlayerStateHelper';
import { delay } from './TaskQueue';
import { LuckyWheelStateHelper } from 'State/Player/LuckyWheel/LuckyWheelStateHelper';
import { InventoryStateHelper } from 'State/Player/Inventory/InventoryStateHelper';
import { IRewardData, RewardType } from 'Utility/RewardHelper';

export default class HarvestTask implements ITask<WorldSystem> {
    private readonly rewardData: IRewardData;
    private readonly coinAmount: number;
    private readonly playAutoFlow: boolean;

    constructor(rewardData: IRewardData, coinAmount: number, playAutoFlow?: boolean) {
        this.rewardData = rewardData;
        this.coinAmount = coinAmount;
        this.playAutoFlow = playAutoFlow ?? false;
    }

    public *execute(system: WorldSystem): IterableIterator<any> {
        let state: IGameState = Game.store.getState();

        PlaySound('harvest-transition');
        PlaySound('harvest-collect');

        system.scene.emit('HarvestCrops', {
            previousCoins: this.coinAmount,
            income: this.rewardData.value,
        });

        state = {
            ...state,
            playerState: InventoryStateHelper.giveReward(
                state.playerState,
                this.rewardData,
                { queueTask: false }
            ),
        };

        this.triggerCoinAnimIfNoFieldsUnlocked(state, this.rewardData.value, system);

        let updatePlayerState = true;
        const ftuxCompleted = FTUXStateHelper.isFtuxCompleted(
            state.playerState,
            FTUXType.HARVEST
        );
        // Set FTUX completed if not already
        if (!ftuxCompleted) {
            state = PlayerStateHelper.setPlayerState(state, FTUXStateHelper.updateFTUXProgress(
                state.playerState,
                [FTUXType.HARVEST]
            ));
            updatePlayerState = true;
        }

        // play auto flow if required / eligible
        if (this.playAutoFlow && !LuckyWheelStateHelper.checkEligibility(state.playerState)) {
            state = PlayerStateHelper.showLevelAutoFlowPopup(state, { delay: 1500 });
            updatePlayerState = true;
        }

        if (updatePlayerState) {
            PlayerActions.setPlayerState(state.playerState);
        }

        yield* delay(3000);
    }

    // Trigger coin animation if no crops unlocked in current world
    // Sam - why is this needed?
    private triggerCoinAnimIfNoFieldsUnlocked(state: IGameState, value: number, system: WorldSystem): void {
        const worldFields = WorldDataHelper.getFieldData(
            state.playerState.maxWorld
        );
        worldFields.sort(
            (a: IFieldData, b: IFieldData) =>
                a.requiredLevel[0] - b.requiredLevel[0]
        );

        const highestLevelCompleted = Game.player.getHighestLevelCompleted(state);
        const noWorldFieldsUnlocked: boolean =
            worldFields.filter(
                (field: IFieldData) =>
                    field.requiredLevel[0] <= highestLevelCompleted
            ).length === 0;
        if (noWorldFieldsUnlocked) {
            const coinText: GameObject = GameObject.findFirstByName(
                WorldHUDHandler.COIN_BAR_BTN_NAME
            );

            if (coinText) {
                hackForceCurrencyAnimation();
                StartAnimationClip(
                    new Counter(coinText, {
                        startValue: state.playerState.inventory.coins,
                        finalValue: state.playerState.inventory.coins + value,
                        onUpdateOverride: function () {
                            (coinText as CoinBar).setCoinsText(this.value.toFixed(0));
                        },
                        delay: 3000,
                        duration: 2000,
                    })
                );
            }
        }
    }
}
